import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { Package, ShieldCheck, ArrowRightLeft } from "lucide-react";
import ItemModal from "@/components/ItemModal";

function ItemGrid({ items, onClick, empty }) {
  if (!items.length) return <p className="text-sm text-muted-foreground py-8 text-center">{empty}</p>;
  return (
    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
      {items.map((it) => {
        const Icon = Icons[it.icon?.replace(/(^|-)([a-z])/g, (_, s, c) => c.toUpperCase())] || Package;
        return (
          <button key={it.id} data-testid={`sdb-item-${it.id}`} onClick={() => onClick(it)}
            className="panel p-2 flex flex-col items-center gap-1 hover:border-primary transition-colors relative">
            <Icon className="w-7 h-7 text-primary" />
            <span className="text-[10px] font-bold text-center leading-none truncate w-full">{it.name}</span>
            {it.qty > 1 && <span className="absolute top-1 right-1 text-[9px] mono bg-muted rounded px-1">x{it.qty}</span>}
          </button>
        );
      })}
    </div>
  );
}

export default function SDB() {
  const { refreshUser } = useOutletContext();
  const [tab, setTab] = useState("inventory");
  const [inv, setInv] = useState([]);
  const [sdb, setSdb] = useState([]);
  const [sel, setSel] = useState(null);

  const load = async () => {
    const [a, b] = await Promise.all([api.get("/inventory"), api.get("/sdb")]);
    setInv(a.data.items); setSdb(b.data.items);
  };
  useEffect(() => { load(); }, []);

  const doAction = async (fn, msg) => {
    try { await fn(); toast.success(msg); await load(); refreshUser?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const actionsFor = (it, inSdb) => inSdb
    ? [{ label: "Withdraw", primary: true, onClick: () => doAction(() => api.post("/sdb/withdraw", { pet_id: it.id }), `${it.name} withdrawn.`) }]
    : [
        { label: "Move to SDB", primary: true, onClick: () => doAction(() => api.post("/sdb/deposit", { pet_id: it.id }), `${it.name} secured in SDB.`) },
        { label: "Discard", onClick: () => doAction(() => api.post("/inventory/discard", { pet_id: it.id }), `${it.name} tossed.`) },
      ];

  const items = tab === "inventory" ? inv : sdb;
  return (
    <div data-testid="sdb-page">
      <span className="label-cap text-primary flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Safety Deposit Box</span>
      <h1 className="font-head font-black text-3xl sm:text-4xl mt-2 mb-1">The Vault Warehouse</h1>
      <p className="text-muted-foreground mb-5 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-400" /> Items stored in the SDB are <b className="text-foreground">immune to theft and negative Random Events</b>.</p>

      <div className="flex gap-1 mb-5 p-1 rounded-[var(--radius)] bg-muted w-fit">
        {[["inventory", "Inventory"], ["sdb", "Safety Deposit Box"]].map(([k, label]) => (
          <button key={k} data-testid={`sdb-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-2 rounded-[var(--radius)] text-xs font-bold uppercase mono ${tab === k ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{label}</button>
        ))}
      </div>

      <div className="panel-glow p-4">
        <ItemGrid items={items} onClick={setSel} empty={tab === "inventory" ? "Your inventory is empty." : "Your SDB is empty. Move items here to keep them safe."} />
      </div>

      <ItemModal item={sel} actions={sel ? actionsFor(sel, tab === "sdb") : []} onClose={() => setSel(null)} />
    </div>
  );
}
